<script>
import { mapGetters } from 'vuex';
import { toast } from 'dashboard/composables/useToast';
import SettingsSectionCard from 'dashboard/components-next/Settings/SettingsSectionCard.vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EmailProviderQuickConfig from './components/EmailProviderQuickConfig.vue';
import InboxesAPI from 'dashboard/api/inboxes';

// Chaves de localStorage usadas pelo seletor "Configuracao rapida".
// _SMTP eh produzido aqui (consumido depois pelo SmtpSettings).
// _IMAP eh consumido aqui (deixado pelo SmtpSettings quando o agente
// configurou SMTP primeiro).
const QUICK_CONFIG_LS_KEY = 'inbox_email_quick_config';
const QUICK_CONFIG_IMAP_KEY = 'inbox_email_quick_config_imap';

export default {
  components: {
    SettingsSectionCard,
    NextButton,
    EmailProviderQuickConfig,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      isIMAPEnabled: false,
      address: '',
      port: '',
      login: '',
      password: '',
      isSSLEnabled: true,
      testing: false,
      testResult: '',
      btnTestLabel: 'Testar conexão',
      btnTestingLabel: 'Testando...',
    };
  },
  validations: {
    address: { required },
    port: { required, minLength: minLength(2) },
    login: { required },
    password: { required },
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      const {
        imap_enabled,
        imap_address,
        imap_port,
        imap_login,
        imap_password,
        imap_enable_ssl,
      } = this.inbox;
      this.isIMAPEnabled = imap_enabled;
      this.address = imap_address;
      this.port = imap_port;
      this.login = imap_login;
      this.password = imap_password;
      this.isSSLEnabled = imap_enable_ssl;

      // Se o agente acabou de escolher um provedor no SMTP, aplica os
      // valores IMAP automaticamente (so a primeira vez). Nao sobrescreve
      // se a inbox ja tem config salva no banco.
      if (!imap_address) {
        this.applyQuickConfigFromStorage();
      }
    },
    applyQuickConfigFromStorage() {
      try {
        const raw = localStorage.getItem(QUICK_CONFIG_IMAP_KEY);
        if (!raw) return;
        const imap = JSON.parse(raw);
        if (imap?.address) this.address = imap.address;
        if (imap?.port) this.port = String(imap.port);
        if (imap?.ssl !== undefined) this.isSSLEnabled = imap.ssl;
        localStorage.removeItem(QUICK_CONFIG_IMAP_KEY);
      } catch (_e) {
        // Ignora — formato invalido ou storage cheio.
      }
    },
    async testImap() {
      this.testing = true;
      this.testResult = '';
      try {
        const { data } = await InboxesAPI.testEmailConnection({
          kind: 'imap',
          config: {
            imap_address: this.address,
            imap_port: this.port,
            imap_login: this.login,
            imap_password: this.password,
            imap_enable_ssl: this.isSSLEnabled,
          },
        });
        this.testResult = data.ok ? 'ok:Conexão OK' : `error:${data.error}`;
      } catch (e) {
        this.testResult = `error:${e?.response?.data?.error || e.message}`;
      } finally {
        this.testing = false;
      }
    },
    applyQuickConfig({ imap, smtp }) {
      // Aplica imediatamente os campos IMAP no form atual.
      if (imap?.address !== undefined) this.address = imap.address;
      if (imap?.port !== undefined) this.port = String(imap.port);
      if (imap?.ssl !== undefined) this.isSSLEnabled = imap.ssl;

      // Guarda o preset SMTP no localStorage pra o SmtpSettings pegar
      // automatico quando o agente trocar de aba.
      try {
        localStorage.setItem(QUICK_CONFIG_LS_KEY, JSON.stringify(smtp));
      } catch (_e) {
        // localStorage indisponivel — sem problema, agente vai digitar
        // manualmente na proxima tela.
      }
    },
    async updateInbox() {
      try {
        this.loading = true;
        let payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            imap_enabled: this.isIMAPEnabled,
            imap_address: this.address,
            imap_port: this.port,
            imap_login: this.login,
            imap_password: this.password,
            imap_enable_ssl: this.isSSLEnabled,
          },
        };

        if (!this.isIMAPEnabled) {
          payload.channel.smtp_enabled = false;
        }

        await this.$store.dispatch('inboxes/updateInboxIMAP', payload);
        toast.success(this.$t('INBOX_MGMT.IMAP.EDIT.SUCCESS_MESSAGE'));
      } catch (error) {
        toast.error(error.message);
      }
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <SettingsSectionCard
      :title="$t('INBOX_MGMT.IMAP.TITLE')"
      :description="$t('INBOX_MGMT.IMAP.SUBTITLE')"
    >
      <form @submit.prevent="updateInbox">
        <label for="toggle-imap-enable">
          <input
            v-model="isIMAPEnabled"
            type="checkbox"
            class="ltr:mr-2 rtl:ml-2"
            name="toggle-imap-enable"
          />
          {{ $t('INBOX_MGMT.IMAP.TOGGLE_AVAILABILITY') }}
        </label>
        <p>{{ $t('INBOX_MGMT.IMAP.TOGGLE_HELP') }}</p>
        <EmailProviderQuickConfig
          v-if="isIMAPEnabled"
          @apply="applyQuickConfig"
        />
        <div v-if="isIMAPEnabled" class="mb-6">
          <woot-input
            v-model="address"
            :class="{ error: v$.address.$error }"
            class="max-w-[75%] w-full"
            :label="$t('INBOX_MGMT.IMAP.ADDRESS.LABEL')"
            :placeholder="$t('INBOX_MGMT.IMAP.ADDRESS.PLACE_HOLDER')"
            @blur="v$.address.$touch"
          />
          <woot-input
            v-model="port"
            type="number"
            :class="{ error: v$.port.$error }"
            class="max-w-[75%] w-full"
            :label="$t('INBOX_MGMT.IMAP.PORT.LABEL')"
            :placeholder="$t('INBOX_MGMT.IMAP.PORT.PLACE_HOLDER')"
            @blur="v$.port.$touch"
          />
          <woot-input
            v-model="login"
            :class="{ error: v$.login.$error }"
            class="max-w-[75%] w-full"
            :label="$t('INBOX_MGMT.IMAP.LOGIN.LABEL')"
            :placeholder="$t('INBOX_MGMT.IMAP.LOGIN.PLACE_HOLDER')"
            @blur="v$.login.$touch"
          />
          <woot-input
            v-model="password"
            :class="{ error: v$.password.$error }"
            class="max-w-[75%] w-full"
            :label="$t('INBOX_MGMT.IMAP.PASSWORD.LABEL')"
            :placeholder="$t('INBOX_MGMT.IMAP.PASSWORD.PLACE_HOLDER')"
            type="password"
            @blur="v$.password.$touch"
          />
          <label for="toggle-enable-ssl">
            <input
              v-model="isSSLEnabled"
              type="checkbox"
              class="ltr:mr-2 rtl:ml-2"
              name="toggle-enable-ssl"
            />
            {{ $t('INBOX_MGMT.IMAP.ENABLE_SSL') }}
          </label>
          <div class="mt-3 flex items-center gap-2">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-n-weak rounded-lg hover:bg-n-slate-2 disabled:opacity-50"
              :disabled="testing || !address || !port || !login || !password"
              @click="testImap"
            >
              <span
                :class="
                  testing
                    ? 'i-lucide-loader-2 animate-spin'
                    : 'i-lucide-plug-zap'
                "
                class="size-3.5"
              />
              {{ testing ? btnTestingLabel : btnTestLabel }}
            </button>
            <span
              v-if="testResult.startsWith('ok:')"
              class="text-xs text-n-teal-11 inline-flex items-center gap-1"
            >
              <span class="i-lucide-check-circle size-3.5" />
              {{ testResult.slice(3) }}
            </span>
            <span
              v-else-if="testResult.startsWith('error:')"
              class="text-xs text-n-ruby-11 inline-flex items-center gap-1"
            >
              <span class="i-lucide-x-circle size-3.5" />
              {{ testResult.slice(6) }}
            </span>
          </div>
        </div>
        <NextButton
          type="submit"
          :label="$t('INBOX_MGMT.IMAP.UPDATE')"
          :is-loading="uiFlags.isUpdatingIMAP"
          :disabled="(v$.$invalid && isIMAPEnabled) || uiFlags.isUpdatingIMAP"
        />
      </form>
    </SettingsSectionCard>
  </div>
</template>
